import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { UserMinus, Check, RotateCcw, RefreshCcw, Clock, Mail } from "lucide-react";

const STATUS_META = {
  scheduled: { label: "Scheduled", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  finalized: { label: "Finalized", cls: "bg-rose-50 text-rose-800 border-rose-200" },
  dismissed: { label: "Kept active", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};
const STATUS_FILTERS = ["scheduled", "finalized", "dismissed"];

export default function AdminMembershipCancellationsTab() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("scheduled");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/membership-cancellations?status=${status}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    const confirmMsg = action === "finalize"
      ? "End this membership NOW? This cancels their Stripe subscription immediately."
      : "Keep this member active and cancel the pending cancellation?";
    if (!window.confirm(confirmMsg)) return;
    setBusy(id);
    try {
      await api.post(`/admin/membership-cancellations/${id}/${action}`, {});
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-membership-cancellations-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-2xl">Membership cancellations</h2>
        <span className="text-xs text-[var(--ff-text-muted)] ml-2">
          Monthly = 30-day written notice + one more charge. Annual = runs to year end, no refund.
        </span>
      </div>

      <div className="flex items-center gap-2 mt-4 flex-wrap" data-testid="mcancel-filter-bar">
        {STATUS_FILTERS.map((s) => {
          const active = status === s;
          return (
            <button key={s} onClick={() => setStatus(s)} data-testid={`mcancel-filter-${s}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${active ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white text-[var(--ff-text-secondary)] border-stone-300 hover:border-[var(--ff-primary)]"}`}>
              {STATUS_META[s].label}
            </button>
          );
        })}
        <button onClick={load} className="ml-auto text-xs inline-flex items-center gap-1 text-[var(--ff-primary)] hover:underline" data-testid="mcancel-refresh">
          <RefreshCcw size={12} /> Refresh
        </button>
      </div>

      {rows === null && <p className="mt-8 text-[var(--ff-text-muted)]" data-testid="mcancel-loading">Loading…</p>}
      {rows?.length === 0 && (
        <div className="mt-8 card-flat p-10 text-center text-[var(--ff-text-muted)]" data-testid="mcancel-empty">
          No requests in this bucket.
        </div>
      )}

      <div className="mt-6 space-y-3">
        {rows?.map((r) => {
          const meta = STATUS_META[r.status] || STATUS_META.scheduled;
          return (
            <div key={r.id} className="card-flat p-5" data-testid={`mcancel-row-${r.id}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${meta.cls}`}>
                      <UserMinus size={11} /> {meta.label}
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-700 capitalize">{r.billing_interval}</span>
                    <span className="text-xs text-[var(--ff-text-muted)] inline-flex items-center gap-1">
                      <Clock size={11} /> {r.requested_at?.slice(0, 16).replace("T", " ")}
                    </span>
                  </div>
                  <p className="font-display text-lg mt-2">{r.name || "Member"}</p>
                  <p className="text-xs text-[var(--ff-text-muted)] inline-flex items-center gap-1"><Mail size={11} /> {r.email}</p>
                  <p className="text-sm mt-2 text-[var(--ff-text-secondary)]">{r.plan_name} · ends {String(r.effective_at || "").slice(0, 10) || "—"}</p>
                  {r.policy && <p className="text-xs mt-2 text-[var(--ff-text-muted)]">{r.policy}</p>}
                  {r.notice_address && <p className="text-xs mt-1 text-[var(--ff-text-muted)]">Notice: {r.notice_address}</p>}
                </div>
                {r.status === "scheduled" && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => act(r.id, "finalize")} disabled={busy === r.id}
                      className="bg-rose-700 hover:bg-rose-800 disabled:opacity-60 text-white px-3 py-2 rounded font-semibold text-xs inline-flex items-center gap-1"
                      data-testid={`mcancel-finalize-${r.id}`}>
                      <Check size={12} /> End now
                    </button>
                    <button onClick={() => act(r.id, "dismiss")} disabled={busy === r.id}
                      className="border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-60 px-3 py-2 rounded font-semibold text-xs inline-flex items-center gap-1"
                      data-testid={`mcancel-dismiss-${r.id}`}>
                      <RotateCcw size={12} /> Keep active
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
